import { useEffect, useState } from "react";
import { Pencil, IndianRupee, Users2, CalendarRange, Loader2 } from "lucide-react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const PLAN_ACCENT = {
  TRIAL: "border-slate-200",
  BASIC: "border-blue-200",
  PRO: "border-teal-300",
  ENTERPRISE: "border-purple-200",
};

function PlanCard({ plan, onEdit }) {
  const accent = PLAN_ACCENT[plan.plan_code] || "border-slate-200";
  return (
    <div
      data-testid={`plan-card-${plan.plan_code}`}
      className={`rounded-xl border-2 ${accent} bg-white shadow-sm p-6 flex flex-col gap-4`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{plan.label}</h3>
          <span
            data-testid={`plan-status-${plan.plan_code}`}
            className={`inline-block mt-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${plan.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}
          >
            {plan.is_active ? "Active — visible on pricing page" : "Inactive — hidden from pricing page"}
          </span>
        </div>
        <Button data-testid={`plan-edit-btn-${plan.plan_code}`} size="icon" variant="outline" onClick={() => onEdit(plan)}>
          <Pencil className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="flex items-center gap-2 text-slate-600">
          <CalendarRange className="h-4 w-4 text-slate-400" />
          <div>
            <div className="text-slate-400 text-xs">Patient/Appointment Limit</div>
            <div className="font-semibold text-slate-900" data-testid={`plan-limit-${plan.plan_code}`}>{plan.monthly_limit}/mo</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <Users2 className="h-4 w-4 text-slate-400" />
          <div>
            <div className="text-slate-400 text-xs">Staff Limit</div>
            <div className="font-semibold text-slate-900" data-testid={`plan-staff-limit-${plan.plan_code}`}>{plan.max_staff}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <IndianRupee className="h-4 w-4 text-slate-400" />
          <div>
            <div className="text-slate-400 text-xs">Monthly Price</div>
            <div className="font-semibold text-slate-900" data-testid={`plan-price-monthly-${plan.plan_code}`}>₹{plan.price_monthly.toLocaleString("en-IN")}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-slate-600">
          <IndianRupee className="h-4 w-4 text-slate-400" />
          <div>
            <div className="text-slate-400 text-xs">Annual Price</div>
            <div className="font-semibold text-slate-900" data-testid={`plan-price-yearly-${plan.plan_code}`}>₹{plan.price_yearly.toLocaleString("en-IN")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ManageSubscriptionsPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/plans").then(({ data }) => setPlans(data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      monthly_limit: plan.monthly_limit,
      max_staff: plan.max_staff,
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      is_active: plan.is_active,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/plans/${editing.plan_code}`, {
        monthly_limit: Number(form.monthly_limit),
        max_staff: Number(form.max_staff),
        price_monthly: Number(form.price_monthly),
        price_yearly: Number(form.price_yearly),
        is_active: form.is_active,
      });
      toast.success(`${editing.label} plan updated — pricing page reflects this immediately`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="manage-subscriptions-page">
        <h1 className="text-2xl font-semibold text-slate-900">Manage Subscription</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Edit each tier's limits and pricing. Changes save to the database immediately and reflect live on the
          public pricing page — no code changes or redeploys needed.
        </p>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="plan-cards-grid">
            {plans.map((plan) => (
              <PlanCard key={plan.plan_code} plan={plan} onEdit={openEdit} />
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="plan-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit {editing?.label} Plan</DialogTitle>
            <DialogDescription>These changes apply to the public pricing page and any new clinic created on this tier.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Patient/Appointment Limit (per month)</Label>
              <Input
                data-testid="plan-edit-monthly-limit"
                type="number"
                min={1}
                value={form.monthly_limit ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, monthly_limit: e.target.value }))}
              />
            </div>
            <div>
              <Label>Staff Account Limit</Label>
              <Input
                data-testid="plan-edit-max-staff"
                type="number"
                min={1}
                value={form.max_staff ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, max_staff: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Price (₹)</Label>
                <Input
                  data-testid="plan-edit-price-monthly"
                  type="number"
                  min={0}
                  value={form.price_monthly ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, price_monthly: e.target.value }))}
                />
              </div>
              <div>
                <Label>Annual Price (₹)</Label>
                <Input
                  data-testid="plan-edit-price-yearly"
                  type="number"
                  min={0}
                  value={form.price_yearly ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, price_yearly: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <Label className="text-sm">Plan Status</Label>
                <p className="text-xs text-slate-500">Inactive tiers are hidden from the public pricing page and from new-clinic selection.</p>
              </div>
              <Switch data-testid="plan-edit-active-toggle" checked={!!form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="plan-edit-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="plan-edit-save-btn">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
